import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { useScenarioStore } from '../../pages/scenario-modeling/scenario-modeling.store';
import { ApiService } from '../../services/index';
import {
  LiquidityCapTableByStakeholder,
  ScenarioCapTableByStakeholder,
  ScenarioType,
} from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export const useCapTableStakeholders = <T extends ScenarioType = ScenarioType.FUNDRAISING>() => {
  const queryClient = useQueryClient();
  const { scenarioId, search } = useScenarioStore();

  const { data, isLoading } = useAppQuery<
    (T extends ScenarioType.FUNDRAISING
      ? ScenarioCapTableByStakeholder
      : LiquidityCapTableByStakeholder)[]
  >({
    queryKey: [QueryKey.CAP_TABLE_STAKEHOLDERS, scenarioId, search],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.SCENARIOS,
          routePath: [scenarioId, 'cap-table', 'stakeholders'],
        },
        { queryParams: { search } },
      ),
    refetchOnWindowFocus: false,
    // Tab remounts must render cached rows instantly — see use-cap-table-stakeholder-by-all.
    staleTime: 30_000,
    enabled: !!scenarioId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CAP_TABLE_STAKEHOLDERS] });
  }, [queryClient]);

  return {
    invalidateQuery,
    data,
    isLoading,
    isEmptySearch: Boolean(search && !data?.length),
  };
};
